import PropTypes from 'prop-types';

import Button from '#globalComponents/Button/Button';

const fmt = (value) => (value ?? 0).toLocaleString('de-DE');

const MAPS_NOTICE =
	'Alle gefundenen Google-Maps-Firmen werden in deine Liste übernommen und mit je einem Credit abgerechnet, auch weniger passende. Keine Vorschau möglich; die Firmen-Obergrenze gilt für Pool und Google Maps zusammen.';
const NATIONWIDE_NOTICE =
	'Die externe Suche läuft dann über ganz Deutschland statt über Ort oder Umkreis des Auftrags und kann die Obergrenze deutlich schneller erreichen.';

/** What a confirmation grants beyond the current limits, as the user must read it. */
const GRANTS = {
	companyLimit: ({ from, to }) => ({ text: `Firmen-Obergrenze von ${fmt(from)} auf ${fmt(to)}` }),
	contactCeiling: ({ from, to }) => ({ text: `Kontakt-Obergrenze von ${fmt(from)} auf ${fmt(to)}` }),
	phone: () => ({ text: 'Telefonnummern aufdecken (Telefon-Add-on)' }),
	mapsEnabled: () => ({ text: 'Google Maps einschalten', notice: MAPS_NOTICE }),
	mapsNationwide: () => ({ text: 'Deutschlandweite Google-Maps-Suche erlauben', notice: NATIONWIDE_NOTICE }),
};

const Grants = ({ widenings }) => (
	<div className="space-y-1.5 rounded-control border border-caution-500/40 bg-caution-200 px-3 py-2 text-xs text-caution-700">
		<p className="font-semibold">Mit „Suche starten“ erteilst du zusätzlich:</p>
		<ul className="list-disc space-y-1 pl-4">
			{widenings.map((widening) => {
				const grant = GRANTS[widening.field]?.(widening) ?? { text: widening.field };
				return (
					<li key={widening.field}>
						<span className="font-semibold">{grant.text}</span>
						{grant.notice && <span className="block">{grant.notice}</span>}
					</li>
				);
			})}
		</ul>
	</div>
);

Grants.propTypes = {
	widenings: PropTypes.arrayOf(
		PropTypes.shape({ field: PropTypes.string.isRequired, from: PropTypes.any, to: PropTypes.any }),
	).isRequired,
};

/**
 * The proposed plan: steps with their credit cost, every permission the confirmation would
 * grant beyond the current limits, the ceiling, and the two ways on. A stale block (overtaken
 * by a chip, profile or message change) only reads.
 */
const PlanBlock = ({ plan, active, stale, widens, onStart, onOpenForm, starting }) => (
	<div className="space-y-3 rounded-card border border-bgBase-500 bg-white p-3.5">
		<div className="space-y-1.5">
			{plan.steps.map((step, index) => (
				<div key={`${step.tool ?? step.label}-${index}`} className="flex items-center gap-2.5 text-sm">
					<span className="grid h-5 w-5 flex-shrink-0 place-items-center rounded-full bg-bgBase-400 text-xs font-semibold text-bgBase-800">
						{index + 1}
					</span>
					<span className="min-w-0 flex-1 truncate text-text">{step.label}</span>
					<span className="whitespace-nowrap text-xs tabular-nums text-bgBase-800">
						{step.cost ? `bis ${fmt(step.cost)} Cr.` : 'kostenlos'}
					</span>
				</div>
			))}
		</div>
		{stale ? (
			<p className="rounded-control bg-bgBase-300 px-2.5 py-1.5 text-xs text-bgBase-800">
				Dieser Vorschlag ist veraltet, bitte neu planen: Der Assistent schlägt mit der nächsten Nachricht neu
				vor.
			</p>
		) : (
			widens &&
			(plan.widenings?.length ? (
				<Grants widenings={plan.widenings} />
			) : (
				<p className="rounded-control bg-caution-200 px-2.5 py-1.5 text-xs text-caution-700">
					Dieser Plan erweitert deine Limits. Mit „Suche starten“ bestätigst du die neuen Obergrenzen.
				</p>
			))
		)}
		<div className="flex items-center justify-between gap-3 border-t border-bgBase-400 pt-2.5">
			<span className="text-xs text-bgBase-800">
				Obergrenze{' '}
				<span className="font-semibold tabular-nums text-text">bis {fmt(plan.estimate?.total)} Credits</span>
				{plan.estimate?.contacts?.max ? ` · bis ${fmt(plan.estimate.contacts.max)} Kontakte` : ''}
			</span>
			<div className="flex gap-2">
				<Button variant="outline" size="sm" onClick={onOpenForm} disabled={!active}>
					In Suchmaske öffnen
				</Button>
				<Button size="sm" onClick={onStart} disabled={!active} loading={starting}>
					Suche starten
				</Button>
			</div>
		</div>
	</div>
);

PlanBlock.propTypes = {
	plan: PropTypes.shape({
		steps: PropTypes.arrayOf(
			PropTypes.shape({ tool: PropTypes.string, label: PropTypes.string, cost: PropTypes.number }),
		).isRequired,
		estimate: PropTypes.object,
		widenings: PropTypes.arrayOf(PropTypes.object),
	}).isRequired,
	active: PropTypes.bool.isRequired,
	stale: PropTypes.bool,
	widens: PropTypes.bool,
	onStart: PropTypes.func.isRequired,
	onOpenForm: PropTypes.func.isRequired,
	starting: PropTypes.bool,
};

export default PlanBlock;
